import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { track } from '@amplitude/analytics-browser'
import { AppPage, Panel, UnderlineTabs, colors } from 'src/components/app-ui'
import {
  getCollectionCourseList,
  getCollectionSettingsAction,
} from 'src/reducers/collection/action'
import { PATH_TABS, OWNER_ONLY_TABS, tabPath } from './tabs'
import CoursesTab from './courses-tab'
import LandingTab from './landing-tab'
import SettingsTab from './settings-tab'
import LearnersTab from './learners-tab'
import CertificationTab from './certification-tab'
import AnalyticsTab from './analytics-tab'
import ShareTab from './share-tab'
import PathTitle from './path-title'

const TAB_COMPONENTS = {
  'mini-courses': CoursesTab,
  'landing-page': LandingTab,
  settings: SettingsTab,
  learners: LearnersTab,
  certification: CertificationTab,
  analytics: AnalyticsTab,
  share: ShareTab,
}

// The list request's callback only runs on success; stop waiting after this.
const LIST_TIMEOUT = 8000

const useStyles = makeStyles({
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '20px',
  },
  skeletonRow: {
    height: 18,
    borderRadius: '6px',
    background: colors.hover,
  },
})

/** Placeholder rows while this path's course list loads. */
const ListSkeleton = () => {
  const classes = useStyles()
  return (
    <Panel className={classes.skeleton}>
      {[72, 56, 64].map(width => (
        <div
          key={width}
          className={classes.skeletonRow}
          style={{ width: `${width}%` }}
        />
      ))}
    </Panel>
  )
}

/** Learning path (collection) detail page with its tabs. */
const PathDetail = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id, page } = useParams()
  const collection = useSelector(state => state.collection.collection)
  const courses = useSelector(state => state.collection.courses)
  const settings = useSelector(state => state.collection.collectionSettings)

  // The path whose course list has loaded. Until then the store may still
  // hold the previously opened path's courses, which must not flash on screen.
  // The page itself never waits: only the course list shows placeholders.
  const [listId, setListId] = useState(null)

  useEffect(() => {
    let active = true
    const loaded = () => active && setListId(id)
    const timer = setTimeout(loaded, LIST_TIMEOUT)
    dispatch(getCollectionCourseList(id, loaded))
    dispatch(getCollectionSettingsAction(id))
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [dispatch, id])
  const listReady = listId === id

  const isCollaborator = Boolean(collection?.isCollaborator)
  const tabs = PATH_TABS.filter(
    tab => !isCollaborator || !OWNER_ONLY_TABS.includes(tab.value),
  )
  const activeTab = tabs.some(tab => tab.value === page) ? page : 'mini-courses'
  const ActiveTab = TAB_COMPONENTS[activeTab]

  const openTab = value => {
    const tab = PATH_TABS.find(t => t.value === value)
    track('collection_page_selector', { pageView: tab.label })
    history.push(tabPath(id, value, tab.subPage))
  }

  // Another path's data may still be in the store for a moment.
  const isThisPath = collection?.id === id
  const courseCount =
    (listReady ? courses?.length : null) ??
    (isThisPath ? collection?.courseCount : null) ??
    0

  return (
    <AppPage
      back={{
        label: 'Learning paths',
        onClick: () => history.push('/collections'),
      }}
      title={
        isThisPath ? (
          <PathTitle collection={collection} editable={!isCollaborator} />
        ) : (
          ''
        )
      }
      subtitle={
        isThisPath || listReady
          ? `${courseCount} ${courseCount === 1 ? 'course' : 'courses'}`
          : ''
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <UnderlineTabs tabs={tabs} value={activeTab} onChange={openTab} />
        {/* Settings-dependent tabs wait for the settings request. */}
        {activeTab === 'mini-courses' && !listReady ? (
          <ListSkeleton />
        ) : (
          (settings || activeTab === 'mini-courses') && <ActiveTab />
        )}
      </div>
    </AppPage>
  )
}

export default PathDetail
